import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import api from '../../utils/api';
import {
  Activity,
  DollarSign,
  Users,
  Home,
  Calendar,
  FileText,
  TrendingUp,
  UserPlus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';

const ActivityList = () => {
  const { user } = useAuth();
  const { canView } = usePermission();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!canView('users')) {
      navigate('/');
      return;
    }

    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities');
      // Backend'den gelen data'yı işle
      const processedActivities = response.data.map(activity => ({
        ...activity,
        icon: getActivityIcon(activity.type),
        color: getActivityColor(activity.type)
      }));
      setActivities(processedActivities);
    } catch (error) {
      // API hazır değilse boş liste göster
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      sale: DollarSign,
      meeting: Users,
      portfolio: Home,
      customer: UserPlus,
      default: Activity
    };
    return icons[type] || icons.default;
  };

  const getActivityColor = (type) => {
    const colors = {
      sale: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
      meeting: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
      portfolio: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400',
      customer: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400',
      default: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'
    };
    return colors[type] || colors.default;
  };

  const formatRelativeTime = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;

    return past.toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Son Aktiviteler</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Sistemdeki tüm aktiviteleri görüntüleyin</p>
      </div>

      {activities.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Activity size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Henüz Aktivite Yok</h3>
            <p className="text-gray-500 dark:text-gray-400">
              Sistemde satış, görüşme, portföy veya müşteri eklendikçe burada görünecek
            </p>
          </div>
        </Card>
      ) : (
        <Card title={`Aktivite Akışı (${activities.length})`}>
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = activity.icon;
              const typeLabel = {
                sale: 'Satış',
                meeting: 'Görüşme',
                portfolio: 'Portföy',
                customer: 'Müşteri'
              }[activity.type] || 'Diğer';

              return (
                <div
                  key={`${activity.type}-${activity.id}`}
                  className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-all duration-200 border border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-md group"
                >
                  <div className={`${activity.color} p-3 rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm">
                        <span className="font-bold text-gray-900 dark:text-gray-100">{activity.user.name}</span>
                        {' '}
                        <span className="text-gray-600 dark:text-gray-400">{activity.action}</span>
                      </p>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${activity.type === 'sale' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                        activity.type === 'meeting' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                          activity.type === 'portfolio' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                            activity.type === 'customer' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                        {typeLabel}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {activity.target}
                    </p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Calendar size={12} />
                      {formatRelativeTime(activity.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ActivityList;
